/**
 * Schémas Zod — carnet de contacts assureur (épopée 9.V.*, chunk 9.V.2, ADR 0030 §5).
 *
 * `insurer_contacts` = personne OU service générique (full_name NULL) rattaché à
 * un assureur. `insurer_contact_emails` = points-email (1 contact → N emails).
 *
 * `status` (active/inactive) piloté par les endpoints activate/deactivate — PAS
 * par PATCH (parité insurers 9.V.1). `is_primary` EST éditable (Create/Patch) :
 * le handler applique la promotion/démotion transactionnelle (service dédié).
 * `insurer_id`/`tenant_id`/`insurer_contact_id` ne sont JAMAIS fournis par le
 * client (dérivés du chemin nested + cohérence DB).
 */

import { z } from "zod";

import { PaginationSchema } from "./common.js";

// ---------------------------------------------------------------------------
// insurer_contacts
// ---------------------------------------------------------------------------

export const CreateInsurerContactSchema = z
  .object({
    // NULL/omis = point de contact générique de service (sans personne nommée).
    full_name: z.string().trim().min(1).max(200).nullable().optional(),
    role: z.string().trim().min(1).max(120).nullable().optional(),
    department: z.string().trim().min(1).max(120).nullable().optional(),
    is_primary: z.boolean().optional(),
  })
  .strict();
export type CreateInsurerContactInput = z.infer<typeof CreateInsurerContactSchema>;

// Pas de `.refine` at-least-one-field : body vide `{}` rejeté par le handler
// (INSURER_CONTACTS_NO_FIELDS_TO_UPDATE 400, parité companies/insurers).
export const UpdateInsurerContactSchema = z
  .object({
    full_name: z.string().trim().min(1).max(200).nullable().optional(),
    role: z.string().trim().min(1).max(120).nullable().optional(),
    department: z.string().trim().min(1).max(120).nullable().optional(),
    is_primary: z.boolean().optional(),
  })
  .strict();
export type UpdateInsurerContactInput = z.infer<typeof UpdateInsurerContactSchema>;

export const InsurerContactFilterSchema = z.object({
  q: z.string().max(100).optional(),
  status: z.enum(["active", "inactive", "all"]).default("active"),
});
export type InsurerContactFilter = z.infer<typeof InsurerContactFilterSchema>;

export const InsurerContactListQuerySchema = PaginationSchema.merge(InsurerContactFilterSchema);
export type InsurerContactListQuery = z.infer<typeof InsurerContactListQuerySchema>;

/**
 * Body (optionnel) de `POST …/contacts/:contactId/deactivate`. Si
 * `replacement_contact_id` est fourni, le serveur promeut ce contact comme
 * nouveau principal ACTIF dans la MÊME transaction que la désactivation de la
 * cible (aucun choix auto silencieux — cf. UI). Absent = désactivation simple.
 */
export const DeactivateInsurerContactSchema = z
  .object({
    replacement_contact_id: z.string().uuid().optional(),
  })
  .strict();
export type DeactivateInsurerContactInput = z.infer<typeof DeactivateInsurerContactSchema>;

// ---------------------------------------------------------------------------
// insurer_contact_emails
// ---------------------------------------------------------------------------

export const CreateInsurerContactEmailSchema = z
  .object({
    email: z.string().trim().min(3).max(254).email(),
    label: z.string().trim().min(1).max(120).nullable().optional(),
    is_primary: z.boolean().optional(),
    preferred_language: z.enum(["fr", "en"]).nullable().optional(),
  })
  .strict();
export type CreateInsurerContactEmailInput = z.infer<typeof CreateInsurerContactEmailSchema>;

export const UpdateInsurerContactEmailSchema = z
  .object({
    email: z.string().trim().min(3).max(254).email().optional(),
    label: z.string().trim().min(1).max(120).nullable().optional(),
    is_primary: z.boolean().optional(),
    preferred_language: z.enum(["fr", "en"]).nullable().optional(),
  })
  .strict();
export type UpdateInsurerContactEmailInput = z.infer<typeof UpdateInsurerContactEmailSchema>;

export const InsurerContactEmailFilterSchema = z.object({
  status: z.enum(["active", "inactive", "all"]).default("active"),
});
export type InsurerContactEmailFilter = z.infer<typeof InsurerContactEmailFilterSchema>;

export const InsurerContactEmailListQuerySchema = PaginationSchema.merge(
  InsurerContactEmailFilterSchema,
);
export type InsurerContactEmailListQuery = z.infer<typeof InsurerContactEmailListQuerySchema>;

/**
 * Body (optionnel) de `POST …/emails/:emailId/deactivate`. Si `replacement_email_id`
 * est fourni, le serveur promeut cet email (même contact parent) comme nouvel email
 * principal ACTIF dans la même transaction que la désactivation de la cible.
 */
export const DeactivateInsurerContactEmailSchema = z
  .object({
    replacement_email_id: z.string().uuid().optional(),
  })
  .strict();
export type DeactivateInsurerContactEmailInput = z.infer<
  typeof DeactivateInsurerContactEmailSchema
>;
